import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  registerDriver: (driverData) => api.post('/auth/register-driver', driverData),
  logout: () => api.post('/auth/logout'),
  getCurrentUser: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  // OAuth
  getGoogleAuthUrl: (redirectUri) => api.get('/auth/oauth/google/url', { params: { redirect_uri: redirectUri } }),
  googleOAuthCallback: (code, state, phone = null) => api.post('/auth/oauth/google/callback', { code, state, phone }),
};

// Products API
export const productsAPI = {
  getAll: () => api.get('/products'),
  getById: (id) => api.get(`/products/${id}`),
  create: (productData) => api.post('/products', productData),
  update: (id, productData) => api.put(`/products/${id}`, productData),
  delete: (id) => api.delete(`/products/${id}`),
  listImages: () => api.get('/products/images/list'),
  uploadImage: (formData) => api.post('/products/images/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Orders API
export const ordersAPI = {
  getAll: () => api.get('/orders'),
  getById: (id) => api.get(`/orders/${id}`),
  create: (orderData) => api.post('/orders', orderData),
  update: (id, orderData) => api.put(`/orders/${id}`, orderData),
  cancel: (id) => api.post(`/orders/${id}/cancel`),
};

// Subscriptions API
export const subscriptionsAPI = {
  getAll: () => api.get('/subscriptions'),
  getById: (id) => api.get(`/subscriptions/${id}`),
  create: (subscriptionData) => api.post('/subscriptions', subscriptionData),
  update: (id, subscriptionData) => api.put(`/subscriptions/${id}`, subscriptionData),
  pause: (id, pauseUntil) => api.post(`/subscriptions/${id}/pause`, { pause_until_date: pauseUntil }),
  resume: (id) => api.post(`/subscriptions/${id}/resume`),
  cancel: (id) => api.post(`/subscriptions/${id}/cancel`),
};

// Deliveries API
export const deliveriesAPI = {
  getAll: () => api.get('/deliveries'),
  getById: (id) => api.get(`/deliveries/${id}`),
  getPending: () => api.get('/deliveries/pending'),
  getMyDeliveries: (status) => api.get('/deliveries/my-deliveries', { params: { status } }),
  assign: (id, driverId) => api.post(`/deliveries/${id}/assign`, { driver_id: driverId }),
  start: (id) => api.post(`/deliveries/${id}/start`),
  complete: (id, notes) => api.post(`/deliveries/${id}/complete`, { driver_notes: notes }),
  uploadPhoto: (id, formData) => api.post(`/deliveries/${id}/upload-photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  fail: (id, notes) => api.post(`/deliveries/${id}/fail`, { notes }),
};

// Users API
export const usersAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (userData) => api.put('/users/profile', userData),
  getAll: (role, page = 1, perPage = 25) => api.get('/users', {
    params: { role, page, per_page: perPage }
  }),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  toggleActive: (id) => api.post(`/users/${id}/toggle-active`),
  uploadLicensePlate: (formData) => api.post('/users/upload-license-plate', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getLicensePlateUrl: (id) => api.get(`/users/${id}/license-plate-url`),
  approveDriver: (id) => api.post(`/users/${id}/approve`),
  denyDriver: (id, reason) => api.post(`/users/${id}/deny`, { reason }),
};

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  createDriver: (driverData) => api.post('/admin/create-driver', driverData),
  getPendingOrders: () => api.get('/admin/orders/pending'),
  createDelivery: (orderId) => api.post(`/admin/orders/${orderId}/create-delivery`),
  getRevenueReport: (startDate, endDate) =>
    api.get('/admin/reports/revenue', { params: { start_date: startDate, end_date: endDate } }),
};

// Pickup Events API
export const pickupEventsAPI = {
  getAll: (params) => api.get('/pickup-events', { params }),
  getById: (id) => api.get(`/pickup-events/${id}`),
  create: (eventData) => api.post('/pickup-events', eventData),
  update: (id, eventData) => api.put(`/pickup-events/${id}`, eventData),
  confirmAdmin: (id) => api.post(`/pickup-events/${id}/confirm-admin`),
  unconfirmAdmin: (id) => api.post(`/pickup-events/${id}/unconfirm-admin`),
  confirmDriver: (id) => api.post(`/pickup-events/${id}/confirm-driver`),
  unconfirmDriver: (id) => api.post(`/pickup-events/${id}/unconfirm-driver`),
  addMessage: (id, message) => api.post(`/pickup-events/${id}/messages`, { message }),
  assignDeliveries: (id, deliveryIds) => api.post(`/pickup-events/${id}/assign-deliveries`, { delivery_ids: deliveryIds }),
  getUpcoming: () => api.get('/pickup-events/upcoming'),
};

export default api;
